import { useState, useEffect, useRef } from "react";
import { T } from "./theme";
import { TrendingUp, TrendingDown, MessageSquare, ThumbsUp, ThumbsDown, Minus, ArrowUpRight, ArrowDownRight, Award } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import PageContainer from "./components/PageContainer";
import PageLoading from "./components/PageLoading";
import Badge from "./components/Badge";
import Card from "./components/Card";

const PERIODOS = [
  { key: "hoje",     label: "Hoje" },
  { key: "semana",   label: "Semana" },
  { key: "mes",      label: "Mês" },
  { key: "semestre", label: "Semestre" },
  { key: "ano",      label: "Ano" },
  { key: "todos",    label: "Todos" },
];

const TIPO_COR   = { promotor: T.green, positivo: T.green, detrator: T.red, negativo: T.red, passivo: T.amber, sugestao: T.amber };
const TIPO_LABEL = { promotor: "Promotor", positivo: "Promotor", detrator: "Detrator", negativo: "Detrator", passivo: "Passivo", sugestao: "Passivo" };

function useCounterAnim(target, duration = 800) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start  = prev.current;
    const end    = parseFloat(target) || 0;
    const frames = Math.max(1, Math.round(duration / 30));
    const step   = (end - start) / frames;
    let current  = start, count = 0;
    const timer = setInterval(() => {
      count++; current += step;
      if (count >= frames) { setDisplay(end); prev.current = end; clearInterval(timer); }
      else setDisplay(current);
    }, 30);
    return () => clearInterval(timer);
  }, [target, duration]);
  return display;
}

function KpiCard({ label, rawValue, sub, delta, icon: Icon, color, isPercent }) {
  const numericTarget = parseFloat(String(rawValue).replace("%", "")) || 0;
  const animated      = useCounterAnim(numericTarget);
  const displayValue  = isPercent
    ? `${animated.toFixed(1)}%`
    : Number.isInteger(numericTarget) && !String(rawValue).includes(".")
      ? Math.round(animated)
      : animated.toFixed(1);

  return (
    <Card hover>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: T.textMid, fontWeight: 500, fontFamily: T.fontBody }}>{label}</span>
        <div style={{
          width: T.iconBoxSize, height: T.iconBoxSize, borderRadius: T.iconBoxRadius,
          background: `${color}15`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: color,
        }}>
          <Icon size={18} strokeWidth={2.5} />
        </div>
      </div>

      <div>
        <div style={{ fontSize: 36, fontWeight: 800, fontFamily: T.fontTitle, color: T.textHi, lineHeight: 1, letterSpacing: "-0.03em" }}>
          {displayValue}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
          {delta !== undefined && delta !== null && (
            <Badge color={delta >= 0 ? T.green : T.red}>
              {delta >= 0 ? <TrendingUp size={12} strokeWidth={3}/> : <TrendingDown size={12} strokeWidth={3}/>}
              {Math.abs(delta).toFixed(1)}
            </Badge>
          )}
          {sub && (
            <span style={{ fontSize: 13, color: T.textLow, fontWeight: 500 }}>
              {sub}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData]             = useState(null);
  const [periodo, setPeriodo]       = useState("mes");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [error, setError]           = useState(null);

  useEffect(() => {
    setError(null);
    fetch(`/api/dashboard-data?periodo=${periodo}`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(`Erro ${r.status}`); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message));
  }, [periodo]);

  const loading = !data && !error;

  if (loading) return <PageContainer><PageLoading label="Carregando dados..." /></PageContainer>;

  if (error) return (
    <PageContainer>
      <div style={{ padding: 20, background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: T.radius, color: T.red, fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 10 }}>
         <span>⚠️</span> Erro ao carregar dados do dashboard: {error}
      </div>
    </PageContainer>
  );

  const { nps = 0, delta = 0, respostas = 0, promotores = 0, detratores = 0, passivos = 0, pctPromotor = 0, pctDetrator = 0, pctPassivo = 0, fidelizacao = [], depoimentos = [], evolucao = [] } = data;

  const kpis = [
    { label: "Score NPS", rawValue: nps.toFixed(1), delta: delta, sub: "vs periodo anterior", icon: Award, color: nps >= 50 ? T.accent : nps >= 0 ? T.amber : T.red },
    { label: "Total de Respostas",  rawValue: respostas, icon: MessageSquare, color: T.blue },
    { label: "Promotores", rawValue: pctPromotor, sub: `${promotores} clientes`, icon: ThumbsUp, color: T.green, isPercent: true },
    { label: "Detratores", rawValue: pctDetrator, sub: `${detratores} clientes`, icon: ThumbsDown, color: T.red, isPercent: true },
    { label: "Passivos",   rawValue: pctPassivo, sub: `${passivos} clientes`, icon: Minus, color: T.amber, isPercent: true },
  ];

  const deps = filtroTipo === "todos" ? depoimentos : depoimentos.filter(d =>
    d.tipo === filtroTipo ||
    (filtroTipo === "promotor" && d.tipo === "positivo") ||
    (filtroTipo === "detrator" && d.tipo === "negativo") ||
    (filtroTipo === "passivo"  && d.tipo === "sugestao")
  );

  return (
    <PageContainer maxWidth={1600}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: T.titleSize, fontWeight: T.titleWeight, fontFamily: T.fontTitle, color: T.textHi, letterSpacing: "-0.03em" }}>Overview</h1>
          <p style={{ margin: "6px 0 0", color: T.textLow, fontSize: T.subtitleSize, fontWeight: 500 }}>
             {data?.unidade ? `Mostrando dados para ${data.unidade}` : 'Acompanhe as metricas de satisfacao e fidelizacao.'}
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, background: T.bgInput, borderRadius: T.radiusSm, padding: 6, border: `1px solid ${T.border}` }}>
          {PERIODOS.map(p => (
            <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
              padding: "8px 16px", borderRadius: T.radiusXs, fontSize: 13, cursor: "pointer",
              background: periodo === p.key ? T.bgCard : "transparent",
              border: "none",
              color: periodo === p.key ? T.textHi : T.textMid,
              fontWeight: periodo === p.key ? 600 : 500,
              boxShadow: periodo === p.key ? "0 2px 6px rgba(0,0,0,0.05)" : "none",
              transition: T.transitionFast, fontFamily: T.fontBody,
              outline: "none",
            }}
            onMouseEnter={e => { if (periodo !== p.key) e.currentTarget.style.color = T.textHi; }}
            onMouseLeave={e => { if (periodo !== p.key) e.currentTarget.style.color = T.textMid; }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 32 }}>
        {kpis.map(kpi => <KpiCard key={kpi.label} {...kpi}/>)}
      </div>

      {/* Evolucao NPS */}
      {evolucao.length > 1 && evolucao.some(e => e.npsGeral != null) && (
        <Card style={{ marginBottom: 24, paddingBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.fontTitle, color: T.textHi, letterSpacing: "-0.01em" }}>Desempenho ao Longo do Tempo</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={evolucao} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorNpsGeral" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={T.green} stopOpacity={0.25}/>
                  <stop offset="95%" stopColor={T.green} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={T.border} />
              <XAxis dataKey="date" tick={{ fill: T.textLow, fontSize: 12, fontWeight: 500 }} tickLine={false} axisLine={false} dy={10} />
              <YAxis domain={[-100, 100]} tick={{ fill: T.textLow, fontSize: 12, fontWeight: 500 }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, color: T.textHi, padding: "10px 14px", boxShadow: T.shadowMd }}
                itemStyle={{ color: T.green, fontWeight: 700 }}
                cursor={{ stroke: T.border, strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area type="monotone" dataKey="npsGeral" stroke={T.green} strokeWidth={3} fillOpacity={1} fill="url(#colorNpsGeral)" name="NPS Geral" activeDot={{ r: 6, fill: T.green, stroke: T.bgCard, strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 24 }}>
        {/* NPS por Area */}
        {fidelizacao.length > 0 && (
          <Card>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.fontTitle, color: T.textHi, marginBottom: 24, letterSpacing: "-0.01em" }}>Ranking por Setor</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {fidelizacao.map((f, idx) => {
                const score    = f.indice;
                const barColor = score >= 50 ? T.green : score >= 0 ? T.amber : T.red;
                const barPct   = Math.max(0, Math.min((score + 100) / 200 * 100, 100));
                const varSign  = (f.variacao || 0) >= 0;
                return (
                  <div key={`${f.area}-${idx}`} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 140, fontSize: 13, fontWeight: 600, color: T.textMid, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.area}</div>
                    <div style={{ flex: 1, height: 10, background: T.bgInput, borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${barPct}%`, background: barColor, borderRadius: 99, transition: "width 1s cubic-bezier(0.16, 1, 0.3, 1)" }}/>
                    </div>
                    <div style={{ width: 50, fontSize: 14, fontWeight: 700, color: T.textHi, textAlign: "right" }}>
                      {score >= 0 ? "+" : ""}{score.toFixed(0)}
                    </div>
                    <div style={{ width: 60, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                       <Badge color={varSign ? T.green : T.red} size="sm">
                          {varSign ? <ArrowUpRight size={10} strokeWidth={3}/> : <ArrowDownRight size={10} strokeWidth={3}/>}
                          {Math.abs(f.variacao || 0).toFixed(1)}
                       </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Ultimos Depoimentos */}
        <Card>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.fontTitle, color: T.textHi, letterSpacing: "-0.01em" }}>Feedback Recente</div>
            <div style={{ display: "flex", gap: 6, background: T.bgInput, padding: 4, borderRadius: T.radiusSm }}>
              {["todos", "promotor", "passivo", "detrator"].map(t => {
                const isActive = filtroTipo === t;
                const cor = t === "promotor" ? T.green : t === "detrator" ? T.red : t === "passivo" ? T.amber : T.textHi;
                return (
                  <button key={t} onClick={() => setFiltroTipo(t)} style={{
                    padding: "6px 14px", borderRadius: T.radiusXs, fontSize: 12, cursor: "pointer",
                    background: isActive ? T.bgCard : "transparent",
                    border: "none",
                    color: isActive ? cor : T.textMid,
                    fontWeight: isActive ? 700 : 500,
                    boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.05)" : "none",
                    transition: T.transitionFast, fontFamily: T.fontBody,
                    outline: "none",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = T.textHi; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = T.textMid; }}
                  >
                    {t === "todos" ? "Todos" : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 400, overflowY: "auto", paddingRight: 4 }}>
            {deps.length === 0 && (
              <div style={{ color: T.textLow, fontSize: 14, textAlign: "center", padding: "40px 20px", background: T.bgInput, borderRadius: T.radius }}>
                Nenhum feedback encontrado para os filtros atuais.
              </div>
            )}
            {deps.map((d, i) => {
              const cor   = TIPO_COR[d.tipo]   || T.border;
              const label = TIPO_LABEL[d.tipo] || d.tipo;
              return (
                <div key={i} style={{
                  padding: "16px 20px",
                  background: T.bg,
                  borderRadius: T.radius,
                  border: `1px solid ${T.border}`,
                  transition: T.transitionFast,
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${cor}50`}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                >
                  <div style={{ display: "flex", gap: 12, marginBottom: 8, alignItems: "center", justifyContent: "space-between" }}>
                    <Badge color={cor} size="sm">{label}</Badge>
                    <span style={{ fontSize: 12, color: T.textLow, fontWeight: 500 }}>{d.data}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: T.textMid, lineHeight: 1.6, fontWeight: 500 }}>"{d.texto}"</p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
